import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { formatApiErrorDetail } from "../lib/api";
import { ArrowUpRight } from "lucide-react";

const Login = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      const u = await login(email, password);
      navigate(u.role === "admin" ? "/admin" : "/app");
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-[hsl(var(--bg))]">
      <div className="hidden lg:block relative">
        <img
          src="https://images.pexels.com/photos/18502918/pexels-photo-18502918.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1200&w=900"
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-tr from-[hsl(var(--primary))]/60 to-transparent" />
        <div className="absolute bottom-10 left-10 right-10 text-white">
          <div className="overline text-white/70">Nashuka Financial Services · Personal Banking</div>
          <div className="font-display text-3xl mt-4 max-w-md leading-tight">
            Welcome back. Your accounts, cards and crypto are waiting.
          </div>
        </div>
      </div>
      <div className="flex flex-col p-8 sm:p-16 justify-center">
        <Link to="/" className="flex items-center gap-3 mb-12" data-testid="login-logo">
          <div className="h-8 w-8 bg-[hsl(var(--primary))] grid place-items-center">
            <span className="text-white font-display text-lg leading-none">N</span>
          </div>
          <div className="font-display text-lg tracking-tight">NASHUKA</div>
        </Link>
        <div className="overline mb-3">Sign in</div>
        <h1 className="font-display text-4xl leading-tight">Welcome back.</h1>
        <p className="text-[hsl(var(--text-muted))] mt-3">
          Don't have an account?{" "}
          <Link to="/register" className="text-[hsl(var(--primary))] underline" data-testid="link-to-register">
            Open one in 60 seconds
          </Link>
          .
        </p>

        <form className="mt-10 max-w-md space-y-5" onSubmit={submit}>
          <div>
            <label className="overline block mb-2">Email</label>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full bg-transparent border border-[hsl(var(--border))] focus:border-[hsl(var(--primary))] outline-none px-4 py-3"
              data-testid="login-email-input"
              autoComplete="email"
            />
          </div>
          <div>
            <label className="overline block mb-2">Password</label>
            <input
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full bg-transparent border border-[hsl(var(--border))] focus:border-[hsl(var(--primary))] outline-none px-4 py-3"
              data-testid="login-password-input"
              autoComplete="current-password"
            />
          </div>
          {err && (
            <div className="text-sm text-[hsl(var(--destructive))]" data-testid="login-error">
              {err}
            </div>
          )}
          <button
            type="submit"
            disabled={busy}
            className="w-full bg-[hsl(var(--primary))] text-white px-4 py-3 hover:bg-[hsl(var(--primary))]/90 disabled:opacity-60 flex items-center justify-center gap-2"
            data-testid="login-submit"
          >
            {busy ? "Signing in…" : "Sign in"} <ArrowUpRight className="w-4 h-4" />
          </button>
        </form>

        <div className="mt-10 text-sm text-[hsl(var(--text-muted))]">
          Admin demo: <span className="font-mono">admin@bank.com</span> /{" "}
          <span className="font-mono">admin123</span>
        </div>
      </div>
    </div>
  );
};

export default Login;
